import { useState, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Logo } from '../../app/Brand';

/** The branded shell every signed-out page sits in (spec 6.5, D96). */
export function AuthCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center bg-band px-4 py-8">
      <div className="w-full max-w-md overflow-hidden rounded-xl border border-line bg-white shadow-sm">
        <div className="flex items-center justify-between gap-4 bg-navy px-6 py-5 text-white">
          <Logo onNavy className="h-8" />
          <span className="text-xs uppercase tracking-[0.14em] text-white/80">Portal</span>
        </div>
        <div className="h-1 bg-gold" />
        <div className="px-6 py-6">
          <h2 className="mb-4 text-base font-semibold text-navy">{title}</h2>
          {children}
        </div>
      </div>
    </div>
  );
}

/** A password box with a show/hide toggle, so a typo is recoverable. */
export function PasswordField({
  id, label, value, onChange, autoComplete, hint, error,
}: {
  id: string;
  label: string;
  value: string;
  onChange(v: string): void;
  autoComplete: string;
  hint?: string;
  error?: string;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted" htmlFor={id}>{label}</label>
      <div className="relative">
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          required
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full rounded-md border px-3 py-2 pr-10 text-sm focus:outline-none ${error ? 'border-danger' : 'border-line focus:border-navy'}`}
        />
        <button
          type="button"
          onClick={() => setShown((s) => !s)}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-ink-muted hover:text-ink"
          title={shown ? 'Hide password' : 'Show password'}
          aria-label={shown ? 'Hide password' : 'Show password'}
        >
          {shown ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : hint ? <p className="mt-1 text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}
